import React from 'react';
import styled from 'styled-components';
import { NavLink } from 'react-router-dom';
import { 
  Users, 
  Plus, 
  BarChart3, 
  Settings, 
  Code,
  Shield,
  Home,
  Calendar,
  MapPin,
  FileText,
  Wrench,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useUI } from '../../contexts/UIContext';
import { DealerUser } from '../../types';

const SidebarContainer = styled.aside<{ $isCollapsed: boolean }>`
  width: ${props => props.$isCollapsed ? '64px' : '256px'};
  background: #424242;
  color: white;
  height: 100vh;
  position: fixed;
  left: 0;
  top: 0;
  z-index: 10;
  overflow-y: auto;
  overflow-x: hidden;
  transition: width 0.3s ease;
  
  /* Hide scrollbar when collapsed */
  ${props => props.$isCollapsed && `
    &::-webkit-scrollbar {
      width: 0;
      display: none;
    }
  `}
`;

const SidebarHeader = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: inherit;
  height: 64px;
  background: #424242;
  border-bottom: 1px solid #5a5a5a;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 0 12px;
  z-index: 11;
`;

const ToggleButton = styled.button`
  background: #5a5a5a;
  border: none;
  color: white;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: #ed2024;
  }
  
  svg {
    width: 20px;
    height: 20px;
  }
`;

const SidebarContent = styled.div`
  padding: 24px 0;
  margin-top: 64px; /* Account for header height */
`;

const NavSection = styled.div`
  margin-bottom: 16px;
  
  &:not(:first-child) {
    border-top: 1px solid #5a5a5a;
    padding-top: 12px;
  }
`;

const SectionTitle = styled.div<{ $isCollapsed?: boolean }>`
  font-size: 0.75rem;
  font-weight: 600;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding: 0 24px 12px;
  white-space: nowrap;
  opacity: ${props => props.$isCollapsed ? '0' : '1'};
  transition: opacity 0.2s ease;
  ${props => props.$isCollapsed && 'height: 0; padding: 0; overflow: hidden;'}
`;

const NavItem = styled(NavLink)<{ $isCollapsed?: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px ${props => props.$isCollapsed ? '20px' : '24px'};
  justify-content: ${props => props.$isCollapsed ? 'center' : 'flex-start'};
  color: #d1d5db;
  text-decoration: none;
  font-weight: 500;
  transition: all 0.2s;
  border-right: 3px solid transparent;
  position: relative;
  
  &:hover {
    background: #ed2024;
    color: white;
  }
  
  &.active {
    background: #ed2024;
    color: white;
    border-right-color: #a01519;
  }
  
  svg {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
  }
  
  .nav-label {
    font-size: 0.9rem;
    white-space: nowrap;
    opacity: ${props => props.$isCollapsed ? '0' : '1'};
    transition: opacity 0.2s ease;
    ${props => props.$isCollapsed && 'width: 0; overflow: hidden;'}
  }
  
  .nav-badge {
    margin-left: auto;
    background: #ef4444;
    color: white;
    font-size: 0.75rem;
    padding: 2px 6px;
    border-radius: 10px;
    min-width: 18px;
    text-align: center;
    opacity: ${props => props.$isCollapsed ? '0' : '1'};
    transition: opacity 0.2s ease;
    ${props => props.$isCollapsed && 'width: 0; overflow: hidden;'}
  }
  
  /* Tooltip on hover when collapsed */
  ${props => props.$isCollapsed && `
    &::after {
      content: attr(title);
      position: absolute;
      left: 64px;
      background: #1f2937;
      color: white;
      padding: 8px 12px;
      border-radius: 6px;
      font-size: 0.875rem;
      white-space: nowrap;
      opacity: 0;
      pointer-events: none;
      transition: opacity 0.2s;
      z-index: 1000;
      border: 1px solid #374151;
    }
    
    &:hover::after {
      opacity: 1;
    }
  `}
`;

const NavAnchor = styled.a<{ $isCollapsed?: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px ${props => props.$isCollapsed ? '20px' : '24px'};
  justify-content: ${props => props.$isCollapsed ? 'center' : 'flex-start'};
  color: #d1d5db;
  text-decoration: none;
  font-weight: 500;
  transition: all 0.2s;
  border-right: 3px solid transparent;
  cursor: pointer;
  position: relative;
  
  &:hover {
    background: #ed2024;
    color: white;
  }
  
  svg {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
  }
  
  .nav-label {
    font-size: 0.9rem;
    white-space: nowrap;
    opacity: ${props => props.$isCollapsed ? '0' : '1'};
    transition: opacity 0.2s ease;
    ${props => props.$isCollapsed && 'width: 0; overflow: hidden;'}
  }
  
  /* Tooltip on hover when collapsed */
  ${props => props.$isCollapsed && `
    &::after {
      content: attr(title);
      position: absolute;
      left: 64px;
      background: #1f2937;
      color: white;
      padding: 8px 12px;
      border-radius: 6px;
      font-size: 0.875rem;
      white-space: nowrap;
      opacity: 0;
      pointer-events: none;
      transition: opacity 0.2s;
      z-index: 1000;
      border: 1px solid #374151;
    }
    
    &:hover::after {
      opacity: 1;
    }
  `}
`;

const Sidebar: React.FC = () => {
  const { user } = useAuth();
  const { isSidebarCollapsed, toggleSidebar, isIframeMode } = useUI();
  const isDealerUser = user?.userType === 'dealer';
  const dealerUser = user as DealerUser;

  if (isDealerUser) {
    // Dealer user sidebar
    return (
      <SidebarContainer $isCollapsed={isSidebarCollapsed}>
        <SidebarHeader>
          <ToggleButton onClick={toggleSidebar} title={isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}>
            {isSidebarCollapsed ? <ChevronRight /> : <ChevronLeft />}
          </ToggleButton>
        </SidebarHeader>
        <SidebarContent>
          {/* Hide Overview in iframe mode */}
          {!isIframeMode && (
            <NavSection>
              <NavItem to="/dealer-dashboard" end $isCollapsed={isSidebarCollapsed} title="Overview">
                <Home />
                <span className="nav-label">Overview</span>
              </NavItem>
            </NavSection>
          )}
          
          <NavSection>
            <NavItem to="/calendar" $isCollapsed={isSidebarCollapsed} title="Calendar">
              <Calendar />
              <span className="nav-label">Calendar</span>
            </NavItem>
          </NavSection>

          <NavSection>
            <NavItem to="/locations" $isCollapsed={isSidebarCollapsed} title="Manage Locations">
              <MapPin />
              <span className="nav-label">Manage Locations</span>
            </NavItem>
          </NavSection>

          <NavSection>
            <NavItem to="/services" $isCollapsed={isSidebarCollapsed} title="Manage Services">
              <Wrench />
              <span className="nav-label">Manage Services</span>
            </NavItem>
          </NavSection>

          {/* Hide Embed Code in iframe mode */}
          {!isIframeMode && (
            <NavSection>
              <NavItem to={`/dealers/${dealerUser?.dealerId}/embed`} $isCollapsed={isSidebarCollapsed} title="Embed Code">
                <Code />
                <span className="nav-label">Embed Code</span>
              </NavItem>
            </NavSection>
          )}
          
          {dealerUser?.role === 'dealer_admin' && (
            <NavSection>
              <NavItem to="/team-users" $isCollapsed={isSidebarCollapsed} title="Team Users">
                <Users />
                <span className="nav-label">Team Users</span>
              </NavItem>
            </NavSection>
          )}
        </SidebarContent>
      </SidebarContainer>
    );
  }

  // Admin user sidebar (original)
  return (
    <SidebarContainer $isCollapsed={isSidebarCollapsed}>
      <SidebarHeader>
        <ToggleButton onClick={toggleSidebar} title={isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}>
          {isSidebarCollapsed ? <ChevronRight /> : <ChevronLeft />}
        </ToggleButton>
      </SidebarHeader>
      <SidebarContent>
        <NavSection>
          <NavItem to="/dealers" $isCollapsed={isSidebarCollapsed} title="All Dealers">
            <Users />
            <span className="nav-label">All Dealers</span>
          </NavItem>
        </NavSection>
        
        <NavSection>
          <NavItem to="/dealers/new" $isCollapsed={isSidebarCollapsed} title="Add New Dealer">
            <Plus />
            <span className="nav-label">Add New Dealer</span>
          </NavItem>
        </NavSection>
        
        <NavSection>
          <NavItem to="/analytics" $isCollapsed={isSidebarCollapsed} title="Usage Analytics">
            <BarChart3 />
            <span className="nav-label">Usage Analytics</span>
          </NavItem>
        </NavSection>
        
        <NavSection>
          <NavItem to="/admin-users" $isCollapsed={isSidebarCollapsed} title="Admin Users">
            <Shield />
            <span className="nav-label">Admin Users</span>
          </NavItem>
        </NavSection>
        
        <NavSection>
          <NavAnchor 
            href="#"
            $isCollapsed={isSidebarCollapsed}
            title="API Documentation"
            onClick={(e: React.MouseEvent) => {
              e.preventDefault();
              const token = localStorage.getItem('adminToken');
              let apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
              
              // Remove /api suffix if present to avoid /api/api-docs
              if (apiUrl.endsWith('/api')) {
                apiUrl = apiUrl.slice(0, -4);
              }
              
              if (token) {
                // Open Swagger UI in new window with token as query parameter
                const swaggerUrl = `${apiUrl}/api-docs?token=${encodeURIComponent(token)}`;
                window.open(swaggerUrl, '_blank', 'noopener,noreferrer');
              } else {
                alert('Please login first to access API documentation.');
              }
            }}
          >
            <FileText />
            <span className="nav-label">API Documentation</span>
          </NavAnchor>
        </NavSection>
      </SidebarContent>
    </SidebarContainer>
  );
};

export default Sidebar;